import { Col, Row, Spin, message } from "antd";
import { findIndex } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useAnalytics } from '../components/hooks/useAnalytics';
import { getCookie } from "../../services/cookies";
import { getApprovedTexts } from "../../services/general";
import { consultationService, validateExams } from "../../services/medicalConsultation";
import { deferredExamService } from "../../services/deferredExam";
import ConsultationEdit from "./components/ConsultationEdit";
import Header from "./components/Header";
import SidebarServices from "./components/SidebarServices";
import DialogflowChat from "./components/DialogflowChat";
import countrySettings from './countrySettings.json';
import allowSignPendingStates from "./constants/allowSignPendingStates";

const MedicalConsultationDetail = () => {
  const [loading, setLoading] = useState({
    list: false,
    detail: false,
    exams: false,
    main: false,
    approvedText: false,
    unfinished: false,
    criterions: false,
    sidebar: false,
  });
  const [servicesList, setServicesList] = useState();
  const [serviceIndex, setServiceIndex] = useState(0);
  const [serviceDetail, setServiceDetail] = useState();
  const [examList, setExamList] = useState();
  const [patientFullName, setPatientFullName] = useState();
  const [isEditingMode, setIsEditingMode] = useState(false);
  const [patientData, setPatientData] = useState();
  const [unfinishedExams, setUnfinishedExams] = useState();
  const [currentFormType, setCurrentFormType] = useState();
  const [medicalConsultationDetails, setMedicalConsultationDetails] = useState();
  const [disableForm, setDisableForm] = useState(false);
  const { medicalConsultationId } = useParams();
  const urlSearchParams = new URLSearchParams(window.location.search);
  const queryParams = Object.fromEntries(urlSearchParams.entries());
  const [urlParams, setUrlParams] = useState(queryParams);
  const [approvedTextList, setApprovedTextList] = useState();
  const [consultationStatusType, setConsultationStatusType] = useState();
  const [validatedExams, setValidatedExams] = useState([]);
  const countryCode = getCookie("country_code");
  const [status, setStatus] = useState();
  const roles = getCookie('roles');
  const isPermittedRole = roles.includes('super_usuario') || roles.includes('equipo_psicologia')
    || roles.includes('doctor') || roles.includes('direccion_medica');
  const reevaluationState = approvedTextList?.find(approvedText => approvedText.status === 'reproved_temporally');
  const [reprovedResultsMap, setReprovedResultsMap] = useState(new Map());
  const [tooltipLoadingStates, setTooltipLoadingStates] = useState(new Map());
  const [generatedPrescriptions, setGeneratedPrescriptions] = useState([]);

  const fetchGeneratedPrescriptions = useCallback(async (medicalConsultationDetailId) => {
    if (!medicalConsultationDetailId) {
      setGeneratedPrescriptions([]);
      return;
    }
    try {
      const response = await consultationService.getGeneratedPrescriptions({
        medical_consultation_detail_id: medicalConsultationDetailId,
      });
      setGeneratedPrescriptions(response || []);
    } catch (error) {
      console.error("Error fetching prescriptions in detail:", error);
      message.error({
          content: 'Error al obtener el listado de prescripciones generadas.',
          style: { marginTop: '35vh' },
        });
    }
  }, []);
  const [hasDeferredExamPrescription, setHasDeferredExamPrescription] = useState(false);
  const [deferredExamPrescriptionState, setDeferredExamPrescriptionState] = useState(null);

  const examListInSidebar = countrySettings.medicalConsultation.examListInSidebar[countryCode];
  const [activeExamTab, setActiveExamTab] = useState(null);
  const [examTabItems, setExamTabItems] = useState([]);
  const [itemHasReprovedValuesFn, setItemHasReprovedValuesFn] = useState(null);
  const examContentRef = useRef(null);
  const sidebarExamSectionRef = useRef(null);
  const [activeConsultationTab, setActiveConsultationTab] = useState('health');

  const hasLoadedExams = examTabItems.length > 0;

  const hasObservedConsultation = servicesList?.some(
    service => service.status === 'reproved_temporally'
  );

  const allConsultationsSigned = servicesList?.every(
    service => service.state === 'signed'
  );

  const { trackEvent } = useAnalytics();

  useEffect(() => {
    if (!examListInSidebar || !hasLoadedExams) return;

    const antLgBreakpoint = 992;
    if (window.innerWidth < antLgBreakpoint) return;

    const examContentAnchorElement = examContentRef.current;
    const sidebarExamSectionElement = sidebarExamSectionRef.current;
    if (!examContentAnchorElement || !sidebarExamSectionElement) return;

    const getDocumentTop = (element) => {
      let top = 0;
      let curr = element;
      while (curr) {
        top += curr.offsetTop;
        curr = curr.offsetParent;
      }
      return top;
    };

    const updateAlignment = () => {
      const currentOffset = parseFloat(
        sidebarExamSectionElement.style.getPropertyValue('--exam-offset') || '0'
      );
      const rawGap = getDocumentTop(examContentAnchorElement) - getDocumentTop(sidebarExamSectionElement);
      const naturalGap = rawGap + currentOffset;
      const newOffset = Math.max(0, naturalGap);
      sidebarExamSectionElement.style.setProperty('--exam-offset', `${newOffset}px`);
    };
    let pendingAlignmentRaf;
    const resizeObserver = new ResizeObserver(() => {
      cancelAnimationFrame(pendingAlignmentRaf);
      pendingAlignmentRaf = requestAnimationFrame(() => requestAnimationFrame(updateAlignment));
    });
    resizeObserver.observe(sidebarExamSectionElement);
    window.addEventListener('resize', updateAlignment);
    const initialRafId = requestAnimationFrame(updateAlignment);
    const sidebarScrollContainer = sidebarExamSectionElement.closest('.sidebar--with-exams')
      || sidebarExamSectionElement.parentElement;

    const scrollSidebarToExamList = () => {
      if (!sidebarScrollContainer) return;
      sidebarScrollContainer.scrollTo({ top: sidebarExamSectionElement.offsetTop, behavior: 'smooth' });
    };

    const scrollSidebarToServices = () => {
      if (sidebarScrollContainer && sidebarScrollContainer.scrollTop > 0) {
        sidebarScrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    const viewportObserver = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (entry.isIntersecting) {
        scrollSidebarToExamList();
      } else if (entry.boundingClientRect.top > 0) {
        scrollSidebarToServices();
      }
    }, { threshold: 0 });
    viewportObserver.observe(examContentAnchorElement);

    return () => {
      cancelAnimationFrame(initialRafId);
      cancelAnimationFrame(pendingAlignmentRaf);
      window.removeEventListener('resize', updateAlignment);
      resizeObserver.disconnect();
      viewportObserver.disconnect();
    };
  }, [hasLoadedExams, examListInSidebar, activeConsultationTab]);

  const handleExamTabItemsUpdate = useCallback((items, hasReprovedFn) => {
    setExamTabItems(items);
    setItemHasReprovedValuesFn(() => hasReprovedFn);
  }, []);

  const getQueryParams = () => {
    if (queryParams.mode || !isPermittedRole) {
      setDisableForm(true);
    }
  };

  const setTooltipLoading = useCallback((serviceId, isLoading) => {
    setTooltipLoadingStates(prev => {
      const newMap = new Map(prev);
      newMap.set(serviceId, isLoading);
      return newMap;
    });
  }, []);

  const fetchServicesList = useCallback(async (params) => {
    setLoading((loading) => { return { ...loading, list: true } });
    try {
      let result;
      let type;
      let response = await consultationService.getServicesList({ id: params.id });
      let safeIndex = 0;

      if (response.length) {
        let indexFound = false;
        if (medicalConsultationId) {
          const indexById = findIndex(response, (item) => {
            return item.medical_consultation_id === parseInt(medicalConsultationId);
          });
          if (indexById !== -1) {
            safeIndex = indexById;
            indexFound = true;
          }
        }
        if (!indexFound && params.serviceId) {
          const findServiceIndexId = response.map((item) => {
            const itemType = item.type?.toLowerCase();
            const targetType = params.serviceType?.toLowerCase();
            if (itemType === targetType || (['battery', 'collection'].includes(itemType) && ['battery', 'collection'].includes(targetType))) {
              return parseInt(item.id)
            }
            return undefined;
          }).indexOf(parseInt(params.serviceId));
          if (findServiceIndexId !== -1) safeIndex = findServiceIndexId;
        }

        setServiceIndex(safeIndex);
        type = response[safeIndex]?.type;
        result = response[safeIndex]?.medical_form_type;
      }

      setCurrentFormType(result !== undefined ? result : null);
      setServicesList(response);
      fetchApprovedTextList(result, type);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading((loading) => { return { ...loading, list: false } });
    }
  }, [urlParams, medicalConsultationId]);

  const fetchExamList = useCallback(
    async (params) => {
      setLoading((loading) => {
        return { ...loading, exams: true };
      });
      const response = await consultationService.getExamList(params);
      const examListCopy = structuredClone(response);
      const serviceDetailCopy = structuredClone(response);

      setExamList(examListCopy);
      setServiceDetail(serviceDetailCopy);
      setLoading((loading) => {
        return { ...loading, exams: false };
      });
    },
    [servicesList]
  );

  const fetchMedicalConsultationDetails = useCallback(async () => {
    setLoading((loading) => { return { ...loading, detail: true } });
    let response;
    try {
      response = await consultationService.getMedicalConsultationDetails({
        id: medicalConsultationId
      });
      setMedicalConsultationDetails(response);
      setUrlParams({
        admissionPatient: response.admission_patient_id,
        serviceId: response.service_id,
        serviceType: response.service_type,
      });
    } catch (error) {
      console.log(error)
    } finally {
      setLoading((loading) => {
        return { ...loading, detail: false };
      });
    } return response;
  }, [medicalConsultationId]);

  const fetchUnfinishedExams = useCallback(async () => {
    let response;
    try {
      setLoading((loading) => ({ ...loading, unfinished: true }));
      response = await consultationService.getUnfishedExams({
        admission_patient_id: urlParams.admissionPatient,
      });
      setUnfinishedExams(response);
    } catch (error) {
      console.error(error);
      message.error({
        content: 'Error al validar exámenes incompletos',
        style: {
          marginTop: '30vh'
        }
      });
    } finally {
      setLoading((loading) => ({ ...loading, unfinished: false }));
      return response;
    }
  }, [urlParams.admissionPatient]);

  const fetchValidateExams = useCallback(async () => {
    try {
      setLoading((_loading) => ({ ..._loading, criterions: true }));
      const response = await validateExams({
        admission_patient_id: urlParams.admissionPatient,
        service_id: urlParams.serviceId,
        service_type: urlParams.serviceType,
      });
      setValidatedExams(response);
    } catch (error) {
      console.error(error);
      message.error({
        content: 'Error al validar exámenes con resultados alterados',
        style: {
          marginTop: '30vh'
        }
      });
    } finally {
      setLoading((_loading) => ({ ..._loading, criterions: false }));
    }
  }, [urlParams.admissionPatient]);

  const confirmRefreshPage = (event) => {
    event.preventDefault();
    const message = "¿Estás seguro que deseas continuar?";
    return (event.returnValue = message);
  };

  const fetchPatientData = useCallback(
    async (params) => {
      setLoading((loading) => {
        return { ...loading, main: true };
      });
      const response = await consultationService.getPatientData({
        admissionPatientId: params.id,
      });
      setPatientData(response[0]);
      setPatientFullName(response[0]["full_name"]);
      setLoading((loading) => {
        return { ...loading, main: false };
      });
    },
    [urlParams]
  );

  useEffect(() => {
    if (
      urlParams.admissionPatient
    ) {
      const params = {
        serviceId: urlParams.serviceId,
        serviceType: urlParams.serviceType,
        id: urlParams.admissionPatient,
      };
      fetchServicesList(params);
    }

    window.addEventListener("beforeunload", confirmRefreshPage);
    return () => {
      window.removeEventListener("beforeunload", confirmRefreshPage);
    };
  }, [fetchServicesList]);

  useEffect(() => {
    if (servicesList && urlParams.admissionPatient) {
      fetchExamList({
        service_id: urlParams.serviceId,
        service_type: urlParams.serviceType,
        admission_patient_id: urlParams.admissionPatient,
      });
    }
  }, [fetchExamList]);

  useEffect(() => {
    getQueryParams();
    if (!medicalConsultationId && urlParams.admissionPatient) {
      consultationService.calledByDoctor({
        admission_patient_id: urlParams.admissionPatient,
      });
    }
    if (urlParams.admissionPatient) {
      fetchPatientData({ id: urlParams.admissionPatient });
    }
  }, [urlParams]);

  useEffect(() => {
    if (medicalConsultationId) {
      fetchMedicalConsultationDetails();
      fetchGeneratedPrescriptions(medicalConsultationId);
    } else {
      setMedicalConsultationDetails();
      setGeneratedPrescriptions([]);
    }
  }, [medicalConsultationId, fetchGeneratedPrescriptions]);

  useEffect(() => {
    if (urlParams.admissionPatient) {
      fetchUnfinishedExams();
      fetchValidateExams();
    }
  }, [fetchValidateExams]);

  const refreshDeferredExamPrescription = useCallback(async () => {
    if (!medicalConsultationId || !countrySettings.medicalConsultation.deferredExam?.[countryCode]) {
      setHasDeferredExamPrescription(false);
      setDeferredExamPrescriptionState(null);
      return;
    }
    try {
      const response = await deferredExamService.getLatestPrescription(medicalConsultationId);
      setHasDeferredExamPrescription(!!response?.data?.prescription);
      setDeferredExamPrescriptionState(response?.data?.prescription?.state || null);
    } catch (error) {
      console.error(error);
    }
  }, [medicalConsultationId, countryCode]);

  useEffect(() => {
    refreshDeferredExamPrescription();
  }, [refreshDeferredExamPrescription]);

  const fetchApprovedTextList = async (medical_form_type, type, multi_sign = false, fromValidation = false) => {
    setLoading(loading => { return { ...loading, approvedText: true } });
    let response;
    try {
      let departmentId = ['psychological_one', 'psychological_two'].includes(medical_form_type?.toLowerCase()) ? 2 : 1;
      response = await getApprovedTexts({
        department_id: departmentId,
        type: type,
        ...((departmentId === 2) && ({ medical_form_type })),
      });
      if (!multi_sign && !fromValidation) {
        setApprovedTextList(response);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(loading => { return { ...loading, approvedText: false } });
      return response;
    }
  };

  const refreshMedicalConsultationDetails = useCallback(() => {
    fetchUnfinishedExams();
    fetchValidateExams();
    fetchExamList({
      service_id: urlParams.serviceId,
      service_type: urlParams.serviceType,
      admission_patient_id: urlParams.admissionPatient,
    });

  }, [fetchExamList, fetchUnfinishedExams, urlParams.admissionPatient, urlParams.serviceId, urlParams.serviceType]);

  const isUnableToChoose = () => (
    validatedExams?.some(exam =>
      (exam.procedures?.some(procedure => (
        procedure.result_type?.includes('reproves')
        || !procedure.result_type
      )))
      ||
      (exam.measurements?.some(measurement => (
        measurement.result_type?.includes('reproves')
        || !measurement.result_type
      )))
    )
  );

  const fetchMedicalConsultationDetail = async (medicalConsultationId) => {
    let response;
    try {
      response = await consultationService.getMedicalConsultationDetails({
        id: medicalConsultationId
      });
    } catch (error) {
      throw error;
    } finally {
      return response;
    }
  };

  const listReproved = useCallback(async (service) => {
    const combined = [];

    try {
      const response = await validateExams({
        admission_patient_id: urlParams.admissionPatient,
        service_id: service.id,
        service_type: service.type,
      });

      response?.forEach(exam => {
        const reprovedProcedures = exam?.procedures?.map(
          (procedure) =>
            (procedure.result_type?.includes("reproves") ||
              procedure.reprovingItems?.includes(
                procedure?.result?.toLowerCase()
              )) && {
              name: procedure.name,
              admissionPatientExamId: exam.id,
              type: "procedure",
              trackName: `${exam.exam_name}:${procedure.name}`,
            }
        );

        const reprovedMeasurements = exam?.measurements?.map(measurement =>
          (measurement?.result_type?.includes('reproves') ||
            measurement.reprovingItems?.includes(measurement?.result?.toLowerCase()))
          && {
            name: measurement.name,
            admissionPatientExamId: exam.id,
            type: 'measurement',
            trackName: `${exam.exam_name}:${measurement.name}`,
          }
        );

        combined.push(
          ...reprovedProcedures.filter(Boolean),
          ...reprovedMeasurements.filter(Boolean)
        );
      });
    } catch (error) {
      console.error(error);
      message.error({
        content: 'Error al validar exámenes con resultados alterados',
        style: {
          marginTop: '30vh'
        }
      });
    }
    return combined;
  }, [urlParams.admissionPatient]);

  const fetchAllReprovedResults = useCallback(async (services) => {
    if (!services || services.length === 0) return;

    try {
      const reprovedMap = new Map();
      const reprovedPromises = services.map(async (service) => {
        if (service?.medical_consultation_id) {
          try {
            setTooltipLoading(service.id, true);
            const reprovedData = await listReproved(service);
            return { serviceId: service.id, reprovedData };
          } catch (error) {
            return { serviceId: service.id, reprovedData: [] };
          } finally {
            setTooltipLoading(service.id, false);
          }
        }
        return { serviceId: service.id, reprovedData: [] };
      });

      const results = await Promise.all(reprovedPromises);

      results.forEach(({ serviceId, reprovedData }) => {
        reprovedMap.set(serviceId, reprovedData || []);
      });

      setReprovedResultsMap(reprovedMap);
    } catch (error) {
      message.error({
        content: 'Error al obtener resultados reprobados.',
        style: {
          marginTop: '40vh'
        }
      });
      console.error('Error fetching reproved results:', error);
    }
  }, [listReproved, setTooltipLoading]);

  useEffect(() => {
    if (servicesList?.length && !loading.list) {
      fetchAllReprovedResults(servicesList);
    }
  }, [servicesList, loading.list, fetchAllReprovedResults]);

  const allowPendingConsultationSign = (pendingReasons) => {
    const allowSignPendingStatesOfCountry = allowSignPendingStates[countryCode] || [];
    return countrySettings.medicalConsultation.allowSignWithPending[countryCode] && Array.isArray(medicalConsultationDetails.pending_reasons) &&
      pendingReasons.every((pendingReason) => allowSignPendingStatesOfCountry.includes(pendingReason));
  }

  const medicalConsultationValidation = async (service, stage) => {
    const medicalConsultation = await fetchMedicalConsultationDetail(service.medical_consultation_id);
    const responseApprovedTextList = await fetchApprovedTextList(service.medical_form_type, service.type, true, true);
    const medicalConsultationStatus = responseApprovedTextList?.find(approvedText =>
      approvedText.id.toString() === medicalConsultation.consultation_result_id?.toString()
    );

    const isMedicalDirection = roles?.includes("direccion_medica");
    const isReprovingRestricted = countrySettings.medicalConsultation.restrictedRoleToSignReprovingValues[countryCode];
    const canSignReproving = !isReprovingRestricted || isMedicalDirection;

    const reproves = await listReproved(service);
    if (
      medicalConsultationStatus &&
      (
        (countrySettings.medicalConsultation.medicalConsultationTracker[countryCode] && medicalConsultationStatus.status === 'approved')
        || (!countrySettings.medicalConsultation.medicalConsultationTracker[countryCode] && medicalConsultationStatus.text === 'Apto')
      )
      && reproves?.length > 0) {
      if (countrySettings.medicalConsultation.medicalConsultationTracker[countryCode] && stage !== 'initial') {
        trackEvent('medical_sign_error', {
          medical_consultation_id: service.medical_consultation_id,
          company_document_number: service.company_document_number,
          service_name: service.name,
          service_type: service.type,
          failed_reason: reproves?.map(reprove => reprove.trackName)?.join(','),
          was_blocked: !canSignReproving,
        });
        if (!canSignReproving) {
          message.error({
            content: `No se puede firmar la consulta médica con resultados reprobatorios`,
            style: {
              marginTop: '40vh'
            }
          });
        }
      }
      return true;
    }
    const unfinished = await fetchUnfinishedExams();
    const unfinishedValidation = (
      unfinished?.length && unfinished.some(exam => {
        return exam?.service_id === medicalConsultation?.service_id
          && exam?.service_type === medicalConsultation?.service_type
          && (exam?.procedures?.length || exam?.measurements?.length)
      })
    );
    const pendingReasons = service.pending_reasons || [];
    const allowPending = countrySettings.medicalConsultation.allowSignWithPending[countryCode] === true;

    const hasPendingState = approvedTextList?.some(state =>
      state?.status === 'pending' &&
      state?.id?.toString() === medicalConsultation?.consultation_result_id?.toString()
    );

    const isPendingReasonsAllowed =
      pendingReasons.length === 0
        ? allowPending
        : allowPending && allowPendingConsultationSign(pendingReasons);

    const shouldShowError = !isPendingReasonsAllowed && ((hasPendingState) || unfinishedValidation);

    if (shouldShowError) {
      if (stage !== 'initial') {
        message.error({
          content: `Exámenes no terminados o consulta pendiente`,
          style: {
            marginTop: '40vh'
          }
        });
      }
      return true;
    }
    return false;
  }

  return (
    <Spin className='cmt-spin'
      spinning={
        loading.detail
        || loading.exams
        || loading.list
        || loading.main
        || loading.approvedText
      }>
      <Row justify="center" className="medical-consultation-detail">
        {
          urlParams.admissionPatient && (
            <Col lg={24} xs={24}>
              {
                patientData && servicesList && (
                  <Header
                    id={urlParams.admissionPatient}
                    serviceId={medicalConsultationDetails ? medicalConsultationDetails.service_id : servicesList.service_id}
                    serviceType={medicalConsultationDetails ? medicalConsultationDetails.service_type : servicesList.service_type}
                    serviceDetail={examList}
                    patientData={patientData}
                    disableForm={disableForm}
                    medicalConsultationDetailId={medicalConsultationId}
                    consultationStatusType={consultationStatusType}
                    status={status}
                    reevaluationState={reevaluationState}
                    countrySettings={countrySettings}
                    countryCode={countryCode}
                    medicalFormType={currentFormType}
                    generatedPrescriptions={generatedPrescriptions}
                    fetchGeneratedPrescriptions={fetchGeneratedPrescriptions}
                    hasObservedConsultation={hasObservedConsultation}
                    allConsultationsSigned={allConsultationsSigned}
                    isConsultationSigned={medicalConsultationDetails?.state === 'signed'}
                    hasDeferredExamPrescription={hasDeferredExamPrescription}
                    refreshDeferredExamPrescription={refreshDeferredExamPrescription}
                  />
                )
              }

              <Row className={examListInSidebar ? 'exam-list-in-sidebar' : ''}>
                {
                  servicesList && (
                    <SidebarServices
                      id={urlParams.admissionPatient}
                      disableForm={disableForm}
                      loading={loading}
                      setLoading={setLoading}
                      servicesList={servicesList}
                      serviceIndex={serviceIndex}
                      unfinishedExams={unfinishedExams}
                      patientData={patientData}
                      countryCode={countryCode}
                      isPermittedRole={isPermittedRole}
                      fetchMedicalConsultationDetails={fetchMedicalConsultationDetails}
                      medicalConsultationValidation={medicalConsultationValidation}
                      reprovedResultsMap={reprovedResultsMap}
                      tooltipLoadingStates={tooltipLoadingStates}
                      examListInSidebar={examListInSidebar}
                      examTabItems={examTabItems}
                      activeExamTab={activeExamTab}
                      setActiveExamTab={setActiveExamTab}
                      itemHasReprovedValues={itemHasReprovedValuesFn}
                      sidebarExamSectionRef={sidebarExamSectionRef}
                      activeConsultationTab={activeConsultationTab}
                      deferredExamPrescriptionState={deferredExamPrescriptionState}
                    />
                  )
                }
                {
                  serviceDetail && servicesList && (
                    <ConsultationEdit
                      id={urlParams.admissionPatient}
                      disableForm={disableForm}
                      loading={loading}
                      examsList={servicesList}
                      serviceIndex={serviceIndex}
                      serviceDetail={serviceDetail}
                      medicalConsultationDetails={medicalConsultationDetails}
                      patientData={patientData}
                      patientFullName={patientFullName}
                      currentFormType={currentFormType}
                      fetchMedicalConsultationDetails={fetchMedicalConsultationDetails}
                      setIsEditingMode={setIsEditingMode}
                      unfinishedExams={unfinishedExams}
                      approvedTextList={approvedTextList}
                      countryCode={countryCode}
                      setConsultationStatusType={setConsultationStatusType}
                      validatedExams={validatedExams}
                      setStatus={setStatus}
                      isPermittedRole={isPermittedRole}
                      urlParams={urlParams}
                      reevaluationState={reevaluationState}
                      refreshMedicalConsultationDetails={refreshMedicalConsultationDetails}
                      countrySettings={countrySettings}
                      isUnableToChoose={isUnableToChoose}
                      medicalConsultationValidation={medicalConsultationValidation}
                      allowPendingConsultationSign={allowPendingConsultationSign}
                      reprovedResultsMap={reprovedResultsMap}
                      examListInSidebar={examListInSidebar}
                      activeExamTab={activeExamTab}
                      setActiveExamTab={setActiveExamTab}
                      setExamTabItems={handleExamTabItemsUpdate}
                      examContentRef={examContentRef}
                      activeConsultationTab={activeConsultationTab}
                      setActiveConsultationTab={setActiveConsultationTab}
                      generatedPrescriptions={generatedPrescriptions}
                      hasDeferredExamPrescription={hasDeferredExamPrescription}
                      deferredExamPrescriptionState={deferredExamPrescriptionState}
                    />
                  )
                }
              </Row>
            </Col>
          )
        }
      </Row>
      {countrySettings.medicalConsultation.showDialogflowChat?.[countryCode] && <DialogflowChat />}
    </Spin>
  );
};

export default MedicalConsultationDetail;